import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Avatar,
  Card,
  CardContent,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton
} from '@mui/material';
import {
  Person,
  Email,
  School,
  AdminPanelSettings,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
// import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from 'notistack';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
// Always use the same baseURL as the API client
const API_ROOT = (api.defaults.baseURL as string) || 'https://capstone-worker.tp071695.workers.dev';

interface ProfileData {
  id: number;
  username: string;
  email: string;
  role: string;
  full_name: string;
  department?: string;
  created_at: string;
  avatar_url?: string;
}

const Profile: React.FC = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [openCamera, setOpenCamera] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { refresh } = useAuth();
  const queryClient = useQueryClient();

  const { data: profile } = useQuery<ProfileData>('profile', async () => {
    const response = await api.get('/auth/me');
    console.log('Profile data received:', response.data.user);
    console.log('Avatar URL:', response.data.user?.avatar_url);
    console.log('API_ROOT:', API_ROOT);
    console.log('Full avatar URL:', response.data.user?.avatar_url ? `${API_ROOT}${response.data.user.avatar_url}` : 'No avatar');
    
    // Test the avatar URL directly
    if (response.data.user?.avatar_url) {
      const testUrl = `${API_ROOT}${response.data.user.avatar_url}`;
      console.log('Testing avatar URL:', testUrl);
      fetch(testUrl)
        .then(res => console.log('Avatar fetch result:', res.status, res.statusText))
        .catch(err => console.error('Avatar fetch error:', err));
    }
    
    return response.data.user;
  });

  const updateProfileMutation = useMutation(
    async ({ id, data }: { id: number; data: Partial<ProfileData> }) => {
      // Update via Users route (Worker API)
      const response = await api.put(`/users/${id}`, data);
      return response.data;
    },
    {
      onSuccess: async () => {
        await refresh();
        queryClient.invalidateQueries('profile');
        enqueueSnackbar('Profile updated successfully!', { variant: 'success' });
        setIsEditing(false);
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to update profile', { variant: 'error' });
      },
    }
  );

  useEffect(() => {
    if (!openCamera) return;
    let stream: MediaStream;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: true });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      } catch (e) {
        enqueueSnackbar('Could not access camera', { variant: 'error' });
        setOpenCamera(false);
      }
    })();
    return () => {
      if (stream) {
        stream.getTracks().forEach(t => t.stop());
      }
    };
  }, [openCamera, enqueueSnackbar]);

  const handleSubmit = (formData: FormData) => {
    const profileData = {
      full_name: formData.get('full_name') as string,
      email: formData.get('email') as string,
      department: formData.get('department') as string,
    };

    if (profile?.id) {
      updateProfileMutation.mutate({ id: profile.id, data: profileData });
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <AdminPanelSettings />;
      case 'supervisor':
        return <School />;
      case 'student':
        return <Person />;
      default:
        return <Person />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'supervisor':
        return 'warning';
      case 'student':
        return 'info';
      default:
        return 'default';
    }
  };

  if (!profile) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={140} sx={{ mb: 3, borderRadius: 2 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Skeleton width="40%" height={32} sx={{ mb: 2 }} />
              <Skeleton height={56} sx={{ mb: 2 }} />
              <Skeleton height={56} sx={{ mb: 2 }} />
              <Skeleton height={56} sx={{ mb: 2 }} />
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 2 }}>
              <Skeleton variant="circular" width={96} height={96} sx={{ mx: 'auto', mb: 2 }} />
              <Skeleton width="60%" sx={{ mx: 'auto' }} />
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Personal Information</Typography>
              <Button
                variant={isEditing ? 'outlined' : 'contained'}
                onClick={() => setIsEditing(!isEditing)}
              >
                {isEditing ? 'Cancel' : 'Edit Profile'}
              </Button>
            </Box>

            <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(new FormData(e.currentTarget)); }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Username"
                    value={profile.username}
                    disabled
                    InputProps={{
                      startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    defaultValue={profile.email}
                    disabled={!isEditing}
                    required
                    InputProps={{
                      startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    name="full_name"
                    defaultValue={profile.full_name}
                    disabled={!isEditing}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Department"
                    name="department"
                    defaultValue={profile.department || ''}
                    disabled={!isEditing}
                  />
                </Grid>
                {isEditing && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button type="submit" variant="contained">
                        Save Changes
                      </Button>
                      <Button variant="outlined" onClick={() => setIsEditing(false)}>
                        Cancel
                      </Button>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                <Avatar
                  key={(profile.avatar_url || 'no-avatar') + (localStorage.getItem('token')?.slice(-6) || '')}
                  src={profile.avatar_url ? `${API_ROOT}${profile.avatar_url}?v=${localStorage.getItem('token')?.slice(-6) || ''}` : undefined}
                  onError={(e) => {
                    console.error('Avatar load error:', e);
                    console.error('Failed to load:', profile.avatar_url ? `${API_ROOT}${profile.avatar_url}` : 'No avatar URL');
                  }}
                  onLoad={() => {
                    console.log('Avatar loaded successfully:', profile.avatar_url ? `${API_ROOT}${profile.avatar_url}` : 'No avatar URL');
                  }}
                  sx={{
                    width: 96,
                    height: 96,
                    bgcolor: `${getRoleColor(profile.role)}.main`,
                    mb: 2,
                  }}
                >
                  {getRoleIcon(profile.role)}
                </Avatar>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button size="small" variant="outlined" onClick={() => setOpenCamera(true)}>Take Photo</Button>
                  <Button size="small" component="label" variant="outlined">
                    Upload
                    <input hidden type="file" accept="image/*" onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const form = new FormData();
                      form.append('avatar', file);
                      try {
                        console.log('Uploading avatar file:', file.name, file.size);
                        const response = await api.post('/auth/profile/avatar', form, { headers: { 'Content-Type': 'multipart/form-data' } });
                        console.log('Upload response:', response.data);
                        enqueueSnackbar('Avatar updated', { variant: 'success' });
                        await refresh();
                        queryClient.invalidateQueries('profile');
                      } catch (err: any) {
                        console.error('Upload error:', err);
                        enqueueSnackbar(err.response?.data?.message || 'Upload failed', { variant: 'error' });
                      }
                    }} />
                  </Button>
                </Box>
                <Typography variant="h6">{profile.full_name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  @{profile.username}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Role
                </Typography>
                <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                  {profile.role}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Member Since
                </Typography>
                <Typography variant="body1">
                  {new Date(profile.created_at).toLocaleDateString()}
                </Typography>
              </Box>

              {profile.department && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Department
                  </Typography>
                  <Typography variant="body1">
                    {profile.department}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={openCamera} onClose={() => setOpenCamera(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Take a photo</DialogTitle>
        <DialogContent>
          <video ref={videoRef} style={{ width: '100%' }} />
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCamera(false)}>Cancel</Button>
          <Button variant="contained" onClick={async () => {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            if (!video || !canvas) return;
            const width = video.videoWidth;
            const height = video.videoHeight;
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) return;
            ctx.drawImage(video, 0, 0, width, height);
            canvas.toBlob(async (blob) => {
              if (!blob) return;
              const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
              const form = new FormData();
              form.append('avatar', file);
              try {
                console.log('Uploading captured photo:', file.name, file.size);
                const response = await api.post('/auth/profile/avatar', form, { headers: { 'Content-Type': 'multipart/form-data' } });
                console.log('Camera upload response:', response.data);
                enqueueSnackbar('Avatar updated', { variant: 'success' });
                queryClient.invalidateQueries('profile');
                setOpenCamera(false);
              } catch (err: any) {
                console.error('Camera upload error:', err);
                enqueueSnackbar(err.response?.data?.message || 'Upload failed', { variant: 'error' });
              }
            }, 'image/jpeg', 0.9);
          }}>Capture</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Profile; 